import {
  breadcrumbs,
  filterConstants,
  nonFilterAttributeTypes,
  searchDefaults,
  searchResultStatus,
  selectors,
} from "../constants/search-constants"
import { FhResponseData } from "../types/search"
import { getUniverse } from "./search-utils"

const createTransformedMeta = (meta: FhResponseData) => {
  const universe = getUniverse(meta)
  const error = universe?.error ?? null
  const facetmap = universe?.facetmap?.find((facet) => facet.universe === searchDefaults.UNIVERSE) ?? null
  const typeSelector = facetmap?.filter?.find((filter) => filter.on === selectors.TYPE_SELECTOR) ?? null
  const categories = getCategories(facetmap)

  //Global filters which come from meta
  const globalFilters = getGlobalFilters(facetmap)

  //Status of the query returned by FH
  const searchPass = meta?.data?.searchpass ?? null

  //Query alternatives, no attribute present if none
  const queryAlternatives = universe?.query_alternatives ?? null

  //Breadcrumbs
  const crumb = universe?.breadcrumbs?.crumb ?? null

  //Breadcrumbs attribute_types
  const breadcrumbsAttributeTypes = universe?.breadcrumbs?.attribute_types ?? null

  //Search term that the query has been executed for
  const searchTerm = getSearchTerm(queryAlternatives, searchPass, crumb)

  //If the search term has been auto corrected by FH, store the original one
  const originalSearchTerm =
    searchPass === searchResultStatus.CORRECTED ? queryAlternatives?.query_suggestion[0]?.original : null

  //Get number of all results
  const nr_of_items_in_selection =
    searchPass === searchResultStatus.HAS_PARTIALS ? 0 : universe?.breadcrumbs?.nr_of_items_in_selection

  //All applied filters
  const breadcrumbFilters = crumb.filter(
    (crumbObj) => crumbObj?.name?.attribute_type && !nonFilterAttributeTypes.has(crumbObj?.name?.attribute_type)
  )

  //Determine all the cases when we show the No Results view
  const showNoResultsView = isNoResultsView(error, searchPass, queryAlternatives, nr_of_items_in_selection)

  //If we need to show the No Results view but there is a combination of filter values applied, show custom No Results view for selected filters
  const showFiltersNoResultsView = showNoResultsView && breadcrumbFilters?.length > 1

  const transformedMeta = {
    error,
    facetmap,
    typeSelector,
    categories,
    globalFilters,
    searchPass,
    queryAlternatives,
    crumb,
    breadcrumbsAttributeTypes,
    searchTerm,
    originalSearchTerm,
    nr_of_items_in_selection,
    breadcrumbFilters,
    showNoResultsView,
    showFiltersNoResultsView,
  }

  return transformedMeta
}

const getCategories = (facetmap) => {
  const allCategoriesSections = facetmap?.filter?.filter((filter) => filter.title === selectors.CATEGORIES)

  //If an error has occured and we don't have any categories return
  if (!allCategoriesSections || allCategoriesSections.length === 0) {
    return []
  }

  // for level 3 we have only one time f.title === 'category'
  if (allCategoriesSections.length === 1) {
    return allCategoriesSections[0].filtersection
  }

  // For level 1 and 2 we get two times f.title === 'category'
  const unselectedCategorySection = allCategoriesSections.find((category) => category?.selected !== true)

  return unselectedCategorySection.filtersection
}

const getGlobalFilters = (facetmap) => {
  const filters = facetmap?.filter?.filter((filter) => {
    const globalFields = filter?.custom_fields?.custom_field?.find((field) => field.value === filterConstants.GLOBAL)

    return globalFields
  })

  return filters
}

const getSearchTerm = (queryAlternatives, searchPass, crumb) => {
  //Use this special breadcrumbcrumb to determine the search term
  const specialCrumb = crumb.find(
    ({ name }) =>
      name.type === breadcrumbs.SEARCH ||
      name.attribute_type === breadcrumbs.CHARACTERISTICS ||
      name.attribute_type === breadcrumbs.APPLICATION_PURPOSES
  )

  //If the search term has been auto corrected get the corrected one from the alternatives
  //otherwise get the search term from a special breadcrumb in the response
  return searchPass === searchResultStatus.CORRECTED
    ? queryAlternatives?.query_suggestion[0]?.value.value
    : specialCrumb
    ? specialCrumb.searchterms?.term?.value || specialCrumb.name?.value
    : null
}

const isNoResultsView = (error, searchPass, queryAlternatives, nr_of_items_in_selection) => {
  //Determine when to show partial results
  //If the meta query status returns OR but we don't have query suggestions, show NoResultsView
  const hasPartials = searchPass === searchResultStatus.HAS_PARTIALS
  const hasQuerySuggestions = queryAlternatives?.query_suggestion?.length > 0

  //If we have an error in the response or we have indication for partial results but no suggestions
  //or we don't have partial results and the meta indicates no results in the selection then show the NoResultsView
  return !!error || (hasPartials && !hasQuerySuggestions) || (!hasPartials && nr_of_items_in_selection === 0)
}

export { createTransformedMeta }
